import React, { useEffect, useMemo, useCallback } from 'react';
import { CartItemLabel, MembershipCardItemLabel, MembershipCardPubs, SelectedTicketFooter, SelectedItemLabel } from '../../molecules/buytickets';
import TranslateManager from 'ts-translate-js';
import { useIFrameResize } from '../../../logic/hooks/utils';
import { useCartInfo } from '../../../mvcapi/hooks';
import { TitleBox } from '@tsinternetsale/common';
import { PleaseWaitSpinner } from '../../../../jsxctl/utilctl';
import { CartItem } from '../../../types/models/mvcapi';
import { useCartControl } from '../../../logic/hooks/use-cart-control';
import { PriceChangeSuggestion } from '../../../mvcapi/models/buyticket';
import { PromoProposalControl } from '../promotions/promo-proposal-control';
import { useAddTicketsToCartStore } from '../../../stores';
import { useAddToCartRequest } from '../../../logic/hooks/ticketselection/use-add-to-cart-request';
import { useSelectedItems } from './use-selected-items';
import { useAddPackageToCartStoreExt } from '../../../stores/add-to-cart-stores/add-package-to-cart-store-ext';

const tr = TranslateManager.boundTR;

const showCart = false;

// INFO: New sale

export function SelectedTicketsManagement() {
    const startTicketRemovalProcessWithConfirm = useAddTicketsToCartStore(s => s.startTicketRemovalProcessWithConfirm);
    const startItemModificationEventDate = useAddTicketsToCartStore(s => s.startItemModification);
    const startItemModificationPackage = useAddPackageToCartStoreExt(s => s.startItemModification); 
    const promoInfoEventDate = useAddTicketsToCartStore(s => s.eventDate?.PromoInfo);
    const promoInfoPackage = useAddPackageToCartStoreExt(s => s.package?.PromoInfo);

    const onPromoUpdated = useAddTicketsToCartStore(s => s.onPromoUpdatedCallback);
    const eventDateId = useAddTicketsToCartStore(s => s.eventDateId);
    const packageId = useAddPackageToCartStoreExt(s => s.packageId);
    const applyEventDatePriceChangeSuggestions = useAddTicketsToCartStore(s => s.applyPriceChangeSuggestions);
    const applyPackagePriceChangeSuggestions = useAddPackageToCartStoreExt(s => s.applyPriceChangeSuggestions);

    const hiddenPromo = useAddTicketsToCartStore(s => s.hiddenPromo); // this could go in the deeper component once the store is implemented in the old package sale also
    const currentWarningReport = useAddTicketsToCartStore(s => s.currentWarningReport);
    const [ membershipTypeSavingAmounts ] = useAddTicketsToCartStore(s => [ s.membershipTypeSavingAmounts, s.setMembershipTypeSavingAmounts ]);
    const ticketSelectionInfoLoading = useAddTicketsToCartStore(s => s.ticketSelectionInfoLoading);

    const isAPackageSale = useAddPackageToCartStoreExt(s => s.isAPackageSale);

    const refreshTicketSelectionInfos = useAddTicketsToCartStore(s => s.refreshTicketSelectionInfo);

    const applyPriceChangeSuggestions = isAPackageSale() ? applyPackagePriceChangeSuggestions : applyEventDatePriceChangeSuggestions;

    const promoInfo = isAPackageSale() ? promoInfoPackage : promoInfoEventDate;

    const selectedItems = useSelectedItems();

    //const [ membershipTypeSavingAmounts, setMembershipTypeSavingAmounts ] = useState<MembershipTypeSaleInfoModel[]>([]);
    //const [ ticketInfosLoading, setTicketInfosLoading ] = useState<boolean>(true);
    // const selectedTickets = useMemo(() => selectedTicketsByEventDateId[eventDateId]?.selectedTickets || [], [selectedTicketsByEventDateId, eventDateId]);

    const [ cart, cartLoading ] = useCartInfo();
    const cartControl = useCartControl();
    
    const [onContentChange] = useIFrameResize();
    
    useEffect(() => {
        refreshTicketSelectionInfos();
    }, [eventDateId, packageId, refreshTicketSelectionInfos, selectedItems]);

    const handleMembershipTypeAddedToCart = useCallback((_membershipTypeId: string, _status: number, priceChangeSuggestions: PriceChangeSuggestion[]) => {
        if(priceChangeSuggestions && priceChangeSuggestions.length > 0)
            applyPriceChangeSuggestions(priceChangeSuggestions);

        if(cartControl)
            cartControl.refreshCart();

        refreshTicketSelectionInfos();
    }, [applyPriceChangeSuggestions, cartControl, refreshTicketSelectionInfos]);


    const startItemModification = useCallback((itemId: string) => {
        if(isAPackageSale())
            startItemModificationPackage(itemId);
        else
            startItemModificationEventDate(itemId);
    }, [isAPackageSale, startItemModificationEventDate, startItemModificationPackage]);

    const ticketLabels = useMemo(() => {
        return selectedItems.map((item) => {
            return <SelectedItemLabel 
                item={item} 
                key={`SelectedTicketLabel_${item._id}`}
                ticketInfosLoading={ticketSelectionInfoLoading}
                onTicketLabelClick={startItemModification}
                onSelectedTicketsRemoved={startTicketRemovalProcessWithConfirm}
            />;
        });
    }, [selectedItems, ticketSelectionInfoLoading, startItemModification, startTicketRemovalProcessWithConfirm]);

    // const disabled = selectedTickets.length === 0 
    //     || ticketSelectionWarningReport.priceListWarnings.length > 0
    //     || ticketSelectionWarningReport.eventDateWarnings.length > 0; 
    const disabled = selectedItems.length === 0 
         || currentWarningReport.priceListWarnings.length > 0
         || currentWarningReport.generalWarnings.length > 0; 

    const membershipCards = useMemo(() => {
        let items: CartItem[] = [];
        if(cart)
            items = cart.Items.filter((item: CartItem) => item.ProductType == 2);
        return items;
    }, [cart]);

    const fakeAddToCartRequest = useAddToCartRequest();

    return <div className='ticket-label-content'>
        {selectedItems.length > 0 && <div>
            {ticketLabels}
            <hr />
            <SelectedTicketFooter 
                items={selectedItems}
                warnings={currentWarningReport.generalWarnings}
                disabled={disabled}
            />
        </div>}
        
        {promoInfo && <PromoProposalControl
            promoInfo={promoInfo}
            hiddenPromo={hiddenPromo}
            showProposalMessage={false}
            onPromoUpdated={onPromoUpdated}
            onContentChange={onContentChange}
            addToCartRequest={fakeAddToCartRequest}
            onPriceChangeSuggestions={applyPriceChangeSuggestions}
            isAPackageSale={isAPackageSale()}
        />}

        {membershipTypeSavingAmounts && <div className="membership-card-pub">
            <MembershipCardPubs 
                membershipTypeSaleInfos={membershipTypeSavingAmounts}
                showBecomeMemberButtons={false}
                showMoreInfosLink={true}
                refreshActiveMembershipCards={handleMembershipTypeAddedToCart}
            />
        </div>}

        {membershipCards.length > 0 && <TitleBox title={tr("Vos cartes de membres")}>
            {cartLoading ? <PleaseWaitSpinner/> : <MembershipCardItemLabel cartItems={membershipCards} />}
        </TitleBox>}

        {showCart && cart?.Items.length > 0 && <TitleBox title={tr("Votre panier")} icon={<i className="fa fa-shopping-cart fa-1x" />}>
            {cartLoading ? <PleaseWaitSpinner/> : <CartItemLabel cart={cart} />}
        </TitleBox>}

    </div>;
}
